import { Grid, Typography, Box, GridSize, Tooltip } from '@mui/material';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { FormGridInput, LabelBox } from './FormGrid.styles';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

interface IFormGridItem {
  name?: string;
  label: ReactNode;
  id: string;
  value?: string | number;
  required?: boolean;
  disabled?: boolean;
  cellSize?: GridSize;
  onSetFieldValue?: (value: string | number) => void;
}
interface IPlanFormGrid {
  formName: string;
  formDescription?: string;
  fields: IFormGridItem[];
  initialValues?: IFieldValues;
  onChangeFieldValues?: (values: IFieldValues) => void;
}

interface IFormItemLabel {
  label?: ReactNode;
  description?: string | false;
}

export type IFieldValues = Record<string, string | number>;

export const FormItemLabel = ({ label, description }: IFormItemLabel) => {
  return (
    label && (
      <LabelBox>
        {label}{' '}
        {description !== false && (
          <Tooltip title={description || ''} arrow placement={'top'}>
            <InfoIcon />
          </Tooltip>
        )}
      </LabelBox>
    )
  );
};

const FormGridItem = ({
  cellSize = 6,
  label,
  value,
  onSetFieldValue,
  name,
  ...props
}: IFormGridItem) => {
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');

  useEffect(() => setCurrentValue(value), [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (/^(0|[1-9][0-9]*)$/.test(newValue) || newValue === '') {
      setCurrentValue(newValue);
    }
  };

  return (
    <Grid item xs={12} sm={cellSize}>
      <FormGridInput
        name={name || props.id}
        {...props}
        value={currentValue}
        error={isError}
        onChange={handleChange}
        onBlur={(e) => {
          const fieldValue = e.target.value.trim();
          if (props.required) {
            setIsError(fieldValue === '');
          }
          onSetFieldValue?.(fieldValue);
        }}
        helperText={isError && t('requiredField')}
        label={label}
        fullWidth
      />
    </Grid>
  );
};
export const FormGrid = ({
  formName,
  formDescription,
  fields,
  initialValues,
  onChangeFieldValues,
}: IPlanFormGrid) => {
  const [currentFieldValues, setCurrentFieldValues] = useState<IFieldValues>({});

  useEffect(() => {
    const initialFieldValues: IFieldValues = {};

    fields.forEach((field) => {
      initialFieldValues[field.id] = field.value ?? initialValues?.[field.id] ?? '';
    });
    setCurrentFieldValues(initialFieldValues);
    onChangeFieldValues?.(initialFieldValues);
  }, [initialValues]);

  const setFieldValue = (fieldId: string, fieldValue: string | number) => {
    const fieldValues = { ...currentFieldValues, [fieldId]: fieldValue };
    setCurrentFieldValues(fieldValues);
    onChangeFieldValues?.(fieldValues);
  };

  return (
    <Box mt={6} data-testid={formName}>
      <Typography variant="h6" textTransform={'capitalize'}>
        <FormItemLabel label={formName} description={formDescription} />
      </Typography>
      <Grid container mt={1} spacing={2}>
        {fields.map((field) => (
          <FormGridItem
            key={field.id}
            onSetFieldValue={(value) => setFieldValue(field.id, value)}
            {...field}
            value={currentFieldValues[field.id]}
          />
        ))}
      </Grid>
    </Box>
  );
};
